import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import benchApi, { BenchEmployee } from '../../api/benchApi';
import { Card } from '../../components/common/Card';
import { Icons } from '../../components/common/Icons';
import { Button } from '../../components/common/Button';
import { SortIcon } from '../../components/common/SortIcon';
import { useSorting, applySorting } from '../../hooks/useSorting';
import './BenchEmployees.css';

const PER_PAGE = 50;

type BenchSortKey = 
  | 'employee_name' 
  | 'category' 
  | 'department_name' 
  | 'manager_name'
  | 'expected_hours'
  | 'billable_hours'
  | 'non_billable_hours'
  | 'total_filled_hours'
  | 'bench_hours'
  | 'utilization';

export function BenchEmployees() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<BenchEmployee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Sorting state
  const { sortConfig, handleSort, getSortDirection, isColumnSorted } = useSorting<BenchSortKey>();
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [departmentFilter, setDepartmentFilter] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Use actual database values for filtering
  const categories = [
    { value: 'ADM', label: 'ADM' },
    { value: 'SA', label: 'Staff Augmentation' },
    { value: 'Support', label: 'Support' },
    { value: 'Intern', label: 'Intern' },
    { value: 'NONE', label: 'None' }
  ];

  // Helper function to get category display label
  const getCategoryLabel = (categoryValue: string | null | undefined): string => {
    if (!categoryValue) return '-';
    const category = categories.find(cat => cat.value === categoryValue);
    return category ? category.label : categoryValue;
  };

  useEffect(() => {
    setCurrentPage(1); // Reset to page 1 when filters change
    fetchBenchEmployees();
  }, [selectedYear, selectedMonth, selectedDate, selectedCategory, departmentFilter]);

  const fetchBenchEmployees = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await benchApi.getBenchEmployees({
        year: selectedYear,
        month: selectedMonth,
        date: selectedDate || undefined,
        department_id: departmentFilter || undefined,
        category: selectedCategory || undefined,
        min_bench_hours: 0
      });
      setEmployees(response.employees);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bench data');
    } finally {
      setIsLoading(false);
    }
  };

  const getUtilizationColor = (utilization: number) => {
    if (utilization >= 80) return '#10b981'; // Green
    if (utilization >= 50) return '#f59e0b'; // Orange
    return '#ef4444'; // Red
  };

  const exportToCSV = () => {
    const headers = [
      'Employee Code',
      'Employee Name',
      'Category',
      'Department',
      'Manager',
      'Expected Hours',
      'Billable Hours',
      'Non-Billable Hours',
      'Total Filled',
      'Bench Hours',
      'Utilization %',
      'Leave Hours'
    ];

    const rows = filteredEmployees.map(emp => [
      emp.employee_code || '',
      emp.employee_name,
      emp.category || '',
      emp.department_name || '',
      emp.manager_name || '',
      emp.expected_hours,
      emp.billable_hours,
      emp.non_billable_hours,
      emp.total_filled_hours,
      emp.bench_hours,
      emp.utilization,
      emp.leave_hours
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const filename = selectedDate 
      ? `bench-employees-${selectedDate}.csv`
      : `bench-employees-${selectedYear}-${selectedMonth.toString().padStart(2, '0')}.csv`;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  // Filter employees based on search query
  const filteredEmployees = useMemo(() => {
    let result = employees;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(emp => 
        emp.employee_name.toLowerCase().includes(query) ||
        emp.employee_code?.toLowerCase().includes(query) ||
        emp.department_name?.toLowerCase().includes(query) ||
        emp.manager_name?.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    result = applySorting(result, sortConfig, (emp, key) => {
      switch (key) {
        case 'employee_name': return emp.employee_name;
        case 'category': return emp.category || '';
        case 'department_name': return emp.department_name || '';
        case 'manager_name': return emp.manager_name || '';
        case 'expected_hours': return emp.expected_hours;
        case 'billable_hours': return emp.billable_hours;
        case 'non_billable_hours': return emp.non_billable_hours;
        case 'total_filled_hours': return emp.total_filled_hours;
        case 'bench_hours': return emp.bench_hours;
        case 'utilization': return emp.utilization;
        default: return '';
      }
    });
    
    return result;
  }, [employees, searchQuery, sortConfig]);

  const totalBenchHours = filteredEmployees.reduce((sum, emp) => sum + emp.bench_hours, 0);
  const avgUtilization = filteredEmployees.length > 0
    ? filteredEmployees.reduce((sum, emp) => sum + emp.utilization, 0) / filteredEmployees.length
    : 0;

  // Pagination calculations (use filtered employees)
  const totalPages = Math.ceil(filteredEmployees.length / PER_PAGE);
  const paginatedEmployees = useMemo(() => {
    const startIndex = (currentPage - 1) * PER_PAGE;
    return filteredEmployees.slice(startIndex, startIndex + PER_PAGE);
  }, [filteredEmployees, currentPage]);

  const startItem = filteredEmployees.length > 0 ? (currentPage - 1) * PER_PAGE + 1 : 0;
  const endItem = Math.min(currentPage * PER_PAGE, filteredEmployees.length);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  return (
    <div className="bench-employees-page">
      {/* Header */}
      <header className="bench-header">
        <div className="bench-header-content">
          <h1>Employees on Bench</h1>
          <p>
            Track employee utilization and bench status for{' '}
            {selectedDate 
              ? new Date(selectedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
              : `${months[selectedMonth - 1]} ${selectedYear}`
            }
            {selectedCategory && ` • Category: ${selectedCategory}`}
          </p>
        </div>
        <div className="bench-header-actions">
          <Button onClick={exportToCSV} variant="secondary" disabled={filteredEmployees.length === 0}>
            <Icons.Download /> Export CSV
          </Button>
        </div>
      </header>

      {/* Filters */}
      <Card className="bench-filters-card" padding="none">
        <div className="bench-filters">
          <div className="filter-group search-filter">
            <label>Search Employees</label>
            <div className="search-input-wrapper">
              <Icons.Search />
              <input 
                type="text" 
                value={searchQuery} 
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, code, department, or manager..."
                className="search-input"
              />
              {searchQuery && (
                <button 
                  className="clear-search-btn" 
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  <Icons.X />
                </button>
              )}
            </div>
          </div>
          <div className="filter-group">
            <label>Month</label>
            <select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
              {months.map((month, idx) => (
                <option key={idx} value={idx + 1}>{month}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Year</label>
            <select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
              {[2024, 2025, 2026, 2027].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Specific Date (Optional)</label>
            <input 
              type="date" 
              value={selectedDate} 
              onChange={(e) => setSelectedDate(e.target.value)}
              placeholder="Select date"
              className="filter-date-input"
            />
            {selectedDate && (
              <button 
                className="clear-date-btn" 
                onClick={() => setSelectedDate('')}
                title="Clear date filter"
              >
                Clear
              </button>
            )}
          </div>
          <div className="filter-group">
            <label>Category</label>
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.value} value={category.value}>{category.label}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Summary Cards */}
      <div className="bench-summary-grid">
        <Card className="bench-summary-card" padding="none">
          <div className="summary-icon" style={{ backgroundColor: '#ef444415', color: '#ef4444' }}>
            <Icons.Users />
          </div>
          <div className="summary-details">
            <span className="summary-value">{filteredEmployees.length}</span>
            <span className="summary-label">Employees on Bench</span>
          </div>
        </Card>
        <Card className="bench-summary-card" padding="none">
          <div className="summary-icon" style={{ backgroundColor: '#f59e0b15', color: '#f59e0b' }}>
            <Icons.Clock />
          </div>
          <div className="summary-details">
            <span className="summary-value">{totalBenchHours.toFixed(0)}</span>
            <span className="summary-label">Total Bench Hours</span>
          </div>
        </Card>
        <Card className="bench-summary-card" padding="none">
          <div className="summary-icon" style={{ backgroundColor: '#3b82f615', color: '#3b82f6' }}>
            <Icons.Activity />
          </div>
          <div className="summary-details">
            <span className="summary-value">{avgUtilization.toFixed(1)}%</span>
            <span className="summary-label">Average Utilization</span>
          </div>
        </Card>
      </div>

      {/* Employees Table */}
      <Card className="bench-table-card" padding="none">
        {isLoading && (
          <div className="bench-loading">
            <div className="spinner" />
            <p>Loading bench data...</p>
          </div>
        )}

        {error && (
          <div className="bench-error">
            <Icons.AlertCircle />
            <p>{error}</p>
            <Button onClick={fetchBenchEmployees} variant="secondary">Retry</Button>
          </div>
        )}

        {!isLoading && !error && employees.length === 0 && (
          <div className="bench-empty">
            <Icons.Users />
            <h3>No employees on bench</h3>
            <p>
              {selectedCategory 
                ? `No ${selectedCategory} employees found on bench for the selected period.`
                : `All employees are fully utilized for ${selectedDate 
                    ? new Date(selectedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                    : `${months[selectedMonth - 1]} ${selectedYear}`
                  }.`
              }
            </p>
          </div>
        )}

        {!isLoading && !error && employees.length > 0 && filteredEmployees.length === 0 && (
          <div className="bench-empty">
            <Icons.Search />
            <h3>No employees found</h3>
            <p>
              No employees match your search "{searchQuery}". Try adjusting your search terms.
            </p>
            <Button onClick={() => setSearchQuery('')} variant="secondary">Clear Search</Button>
          </div>
        )}

        {!isLoading && !error && filteredEmployees.length > 0 && (
          <div className="bench-table-wrapper">
            <table className="bench-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('employee_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Employee
                    <SortIcon direction={getSortDirection('employee_name')} isActive={isColumnSorted('employee_name')} />
                  </th>
                  <th onClick={() => handleSort('category')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Category
                    <SortIcon direction={getSortDirection('category')} isActive={isColumnSorted('category')} />
                  </th>
                  <th onClick={() => handleSort('department_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Department
                    <SortIcon direction={getSortDirection('department_name')} isActive={isColumnSorted('department_name')} />
                  </th>
                  <th onClick={() => handleSort('manager_name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Manager
                    <SortIcon direction={getSortDirection('manager_name')} isActive={isColumnSorted('manager_name')} />
                  </th>
                  <th onClick={() => handleSort('expected_hours')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Expected
                    <SortIcon direction={getSortDirection('expected_hours')} isActive={isColumnSorted('expected_hours')} />
                  </th>
                  <th onClick={() => handleSort('billable_hours')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Billable
                    <SortIcon direction={getSortDirection('billable_hours')} isActive={isColumnSorted('billable_hours')} />
                  </th>
                  <th onClick={() => handleSort('non_billable_hours')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Non-Billable
                    <SortIcon direction={getSortDirection('non_billable_hours')} isActive={isColumnSorted('non_billable_hours')} />
                  </th>
                  <th onClick={() => handleSort('total_filled_hours')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Total Filled
                    <SortIcon direction={getSortDirection('total_filled_hours')} isActive={isColumnSorted('total_filled_hours')} />
                  </th>
                  <th onClick={() => handleSort('bench_hours')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Bench Hours
                    <SortIcon direction={getSortDirection('bench_hours')} isActive={isColumnSorted('bench_hours')} />
                  </th>
                  <th onClick={() => handleSort('utilization')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                    Utilization
                    <SortIcon direction={getSortDirection('utilization')} isActive={isColumnSorted('utilization')} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedEmployees.map((emp) => (
                  <tr key={emp.employee_id}>
                    <td>
                      <div className="employee-cell">
                        <strong>{emp.employee_name}</strong>
                        <span className="employee-code">{emp.employee_code}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`category-badge ${emp.category?.toLowerCase().replace(/\s+/g, '-')}`}>
                        {getCategoryLabel(emp.category)}
                      </span>
                    </td>
                    <td>{emp.department_name || '-'}</td>
                    <td>{emp.manager_name || '-'}</td>
                    <td className="number-cell">{emp.expected_hours}h</td>
                    <td className="number-cell billable">{emp.billable_hours}h</td>
                    <td className="number-cell non-billable">{emp.non_billable_hours}h</td>
                    <td className="number-cell">{emp.total_filled_hours}h</td>
                    <td className="number-cell bench-hours">{emp.bench_hours}h</td>
                    <td>
                      <div className="utilization-cell">
                        <div className="utilization-bar-bg">
                          <div
                            className="utilization-bar-fill"
                            style={{
                              width: `${Math.min(emp.utilization, 100)}%`,
                              backgroundColor: getUtilizationColor(emp.utilization)
                            }}
                          />
                        </div>
                        <span className="utilization-text" style={{ color: getUtilizationColor(emp.utilization) }}>
                          {emp.utilization.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {!isLoading && !error && totalPages > 1 && (
        <div className="bench-pagination">
          <Button
            variant="ghost"
            size="sm"
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Previous
          </Button>
          <span>
            <strong>{startItem}-{endItem}</strong> of <strong>{filteredEmployees.length}</strong>
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export default BenchEmployees;
